import { Fragment, useEffect, type ComponentType, type ReactElement, type ReactNode } from 'react';

type SlotMarkerProps = {
  component?: unknown;
  children?: ReactNode;
  [prop: string]: unknown;
};

type SlotMapBucket = Record<string | number, unknown>;

type SlotRegistryDraft = Record<string, null | unknown | SlotMapBucket>;

/** Single slots store `props.component` at `key`; map slots store under `registry[key][props[mapBy]]`. */
export type CompoundSlotField<TRegistry extends object, K extends keyof TRegistry & string = keyof TRegistry & string> =
  | {
      key: K;
      type: ComponentType<any>;
      mapBy?: undefined;
    }
  | {
      key: K;
      type: ComponentType<any>;
      mapBy: string;
    };

export type UseCollectCompoundSlotsParams<TRegistry extends object> = {
  children: ReactNode;
  fields: readonly CompoundSlotField<TRegistry>[];
  setter: (slots: TRegistry) => void;
};

const isMapBucket = (value: unknown): value is SlotMapBucket =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isSlotElement = (node: object): node is ReactElement<SlotMarkerProps> => 'type' in node && 'props' in node;

const createEmptySlots = <TRegistry extends object>(
  fields: readonly CompoundSlotField<TRegistry>[],
): SlotRegistryDraft => {
  const slots: SlotRegistryDraft = {};

  for (const field of fields) {
    slots[field.key] = field.mapBy ? {} : null;
  }

  return slots;
};

export const collectCompoundSlots = <TRegistry extends object>(
  children: ReactNode,
  fields: readonly CompoundSlotField<TRegistry>[],
): TRegistry => {
  const slots = createEmptySlots(fields);
  const fieldByType = new Map(fields.map(field => [field.type, field]));

  const visit = (node: ReactNode): void => {
    if (!node) return;

    if (Array.isArray(node)) {
      node.forEach(visit);

      return;
    }

    if (typeof node !== 'object' || !isSlotElement(node)) return;

    if (node.type === Fragment) {
      visit(node.props.children);

      return;
    }

    if (typeof node.type === 'string') return;

    const field = fieldByType.get(node.type);

    if (!field) return;

    if (field.mapBy) {
      const mapKey = node.props[field.mapBy];

      if (typeof mapKey !== 'string' && typeof mapKey !== 'number') return;

      const bucket = slots[field.key];

      if (!isMapBucket(bucket)) return;

      bucket[mapKey] = node.props.component;

      return;
    }

    slots[field.key] = node.props.component;
  };

  visit(children);

  return slots as TRegistry;
};

export const useCollectCompoundSlots = <TRegistry extends object>({
  children,
  fields,
  setter,
}: UseCollectCompoundSlotsParams<TRegistry>): void => {
  useEffect(() => {
    setter(collectCompoundSlots(children, fields));
  }, [children, fields, setter]);
};
